import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Schedule } from '../types/schedule';
import { getSchedules, createSchedule, updateSchedule, deleteSchedule } from '../services/api';

interface ScheduleContextType {
  schedules: Schedule[];
  loading: boolean;
  error: string | null;
  fetchSchedules: () => Promise<void>;
  addSchedule: (schedule: Schedule) => Promise<boolean>;
  editSchedule: (
    id: number,
    content: string,
    end_date: string | null,
    password: string,
    category: '교역자' | '목자' | '기타'
  ) => Promise<boolean>;
  removeSchedule: (id: number, password: string) => Promise<boolean>;
  getSchedulesByDate: (date: string) => Schedule[];
}

const ScheduleContext = createContext<ScheduleContextType | undefined>(undefined);

export const useSchedules = () => {
  const context = useContext(ScheduleContext);
  if (context === undefined) {
    throw new Error('useSchedules must be used within a ScheduleProvider');
  }
  return context;
};

interface ScheduleProviderProps {
  children: ReactNode;
}

export const ScheduleProvider: React.FC<ScheduleProviderProps> = ({ children }) => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSchedules = async () => {
    setLoading(true);
    try {
      const data = await getSchedules();
      setSchedules(data);
      setError(null);
    } catch (err) {
      setError('일정을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const addSchedule = async (schedule: Schedule): Promise<boolean> => {
    setLoading(true);
    try {
      const response = await createSchedule(schedule);
      if (response.success && response.id) {
        setSchedules([...schedules, { ...schedule, id: response.id }]);
        return true;
      } else {
        setError(response.message || '일정 추가에 실패했습니다.');
        return false;
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const editSchedule = async (
    id: number,
    content: string,
    end_date: string | null,
    password: string,
    category: '교역자' | '목자' | '기타'
  ): Promise<boolean> => {
    setLoading(true);
    try {
      const response = await updateSchedule(id, { content, end_date, password, category });
      if (response.success) {
        setSchedules(
          schedules.map((schedule) =>
            schedule.id === id ? { ...schedule, content, end_date, category } : schedule
          )
        );
        return true;
      } else {
        setError(response.message || '일정 수정에 실패했습니다.');
        return false;
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeSchedule = async (id: number, password: string): Promise<boolean> => {
    setLoading(true);
    try {
      const response = await deleteSchedule(id, { password });
      if (response.success) {
        setSchedules(schedules.filter((schedule) => schedule.id !== id));
        return true;
      } else {
        setError(response.message || '일정 삭제에 실패했습니다.');
        return false;
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getSchedulesByDate = (date: string): Schedule[] => {
    return schedules.filter((schedule) => {
      // 단일 날짜 일정
      if (schedule.date === date && !schedule.end_date) {
        return true;
      }

      // 기간 설정된 일정
      if (schedule.end_date) {
        const targetDate = new Date(date);
        const startDate = new Date(schedule.date);
        const endDate = new Date(schedule.end_date);

        // 시작일과 종료일 사이에 있는 경우
        return targetDate >= startDate && targetDate <= endDate;
      }

      return false;
    });
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  return (
    <ScheduleContext.Provider
      value={{
        schedules,
        loading,
        error,
        fetchSchedules,
        addSchedule,
        editSchedule,
        removeSchedule,
        getSchedulesByDate,
      }}
    >
      {children}
    </ScheduleContext.Provider>
  );
};
